/**
 * Unlocks a gated video elsewhere on the site and sends the visitor to it.
 *
 * The key is written to the slot interact/campaign-hero-video reads, so the video
 * opens unlocked on arrival and stays unlocked on return visits. Note that
 * interact/video-demo keys the same storage prefix by HubSpot form id instead, so
 * it is unlocked by its own nested form rather than by this field.
 */

const STORAGE_PREFIX = 'interact:video-demo-unlocked:';

interface HsFormCallbackData {
  type: 'hsFormCallback';
  eventName: string;
  id?: string;
  data?: { formGuid?: string };
}

function isHsFormCallback(data: unknown): data is HsFormCallbackData {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>).type === 'hsFormCallback'
  );
}

function initGate(block: HTMLElement): void {
  const unlockKey = block.dataset.unlockKey || '';
  const redirectUrl = block.dataset.redirectUrl || '';
  const formId = block.getAttribute('data-form-id') || '';

  if (!unlockKey && !redirectUrl) return;

  window.addEventListener('message', (event: MessageEvent) => {
    if (!isHsFormCallback(event.data) || event.data.eventName !== 'onFormSubmitted') return;

    // Scope to this block's form when both ids are known; a page can hold several.
    const submittedId = event.data.id || event.data.data?.formGuid || '';
    if (formId && submittedId && submittedId !== formId) return;

    // An unidentified submit could be any form on the page, so it must not navigate.
    const ambiguous =
      !submittedId &&
      document.querySelectorAll('.wp-block-interact-hubspot-form[data-form-id]').length > 1;

    if (unlockKey) {
      try {
        localStorage.setItem(`${STORAGE_PREFIX}${unlockKey}`, '1');
      } catch {
        // Storage blocked (private mode) — the redirect still happens, and the
        // video page falls back to its locked state.
      }
    }

    if (redirectUrl && !ambiguous) {
      window.location.href = redirectUrl;
    }
  });
}

function init(): void {
  document
    .querySelectorAll<HTMLElement>(
      '.wp-block-interact-hubspot-form[data-unlock-key], .wp-block-interact-hubspot-form[data-redirect-url]',
    )
    .forEach(initGate);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export {};
